import { useState, useEffect, useRef } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { useAuth } from "@/contexts/AuthContext";
const API_URL = process.env.REACT_APP_BACKEND_URL || "";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { GraftonChat } from "@/components/GraftonChat";
import {
  Users, FileText, Plus, Upload, Search, Star,
  Sparkles, Briefcase, MapPin, Clock, Eye,
  Trash2, UserPlus, Filter, BarChart3,
  CheckCircle2, XCircle, AlertCircle, Bot,
  Radar, Globe, Linkedin, TrendingUp, Loader2,
  Zap, Target, ExternalLink,
} from "lucide-react";

type Candidate = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  cv_url: string | null;
  score: number;
  status: string;
  source: string;
  notes: string;
  job_posting_id: string | null;
  created_at: string;
};

type JobPosting = {
  id: string;
  title: string;
  description: string;
  requirements: string;
  location: string;
  contract_type: string;
  salary_range: string;
  status: string;
  applications_count: number;
  created_at: string;
};

type VeilleProfile = {
  id: string;
  name: string;
  title: string;
  location: string;
  score: number;
  source: "linkedin" | "indeed" | "welcometothejungle" | "apec";
  skills: string[];
  matchReason: string;
};

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: typeof CheckCircle2 }> = {
  new: { label: "Nouveau", color: "bg-blue-500/10 text-blue-400 border-blue-500/20", icon: AlertCircle },
  screening: { label: "Présélection", color: "bg-amber-500/10 text-amber-400 border-amber-500/20", icon: Eye },
  interview: { label: "Entretien", color: "bg-purple-500/10 text-purple-400 border-purple-500/20", icon: Users },
  hired: { label: "Recruté", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", icon: CheckCircle2 },
  rejected: { label: "Refusé", color: "bg-red-500/10 text-red-400 border-red-500/20", icon: XCircle },
};

const JOB_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  draft: { label: "Brouillon", color: "bg-muted text-muted-foreground" },
  published: { label: "Publiée", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  closed: { label: "Clôturée", color: "bg-red-500/10 text-red-400 border-red-500/20" },
};

const SOURCE_ICONS: Record<string, { icon: typeof Globe; color: string; label: string }> = {
  linkedin: { icon: Linkedin, color: "text-blue-500", label: "LinkedIn" },
  indeed: { icon: Globe, color: "text-purple-500", label: "Indeed" },
  welcometothejungle: { icon: Globe, color: "text-amber-500", label: "WTTJ" },
  apec: { icon: Globe, color: "text-emerald-500", label: "APEC" },
};

// Simulated veille profiles for demo
const generateVeilleProfiles = (jobs: JobPosting[]): VeilleProfile[] => {
  return []; // pas de profils simulés : à connecter à une vraie veille quand l'équipe existera
  // eslint-disable-next-line no-unreachable
  const names = [
    { name: "Marie Dupont", title: "Développeuse Full-Stack Senior", location: "Paris", skills: ["React", "Node.js", "TypeScript", "PostgreSQL"] },
    { name: "Thomas Martin", title: "Chef de Projet Digital", location: "Lyon", skills: ["Agile", "Scrum", "Jira", "Leadership"] },
    { name: "Sarah Leroy", title: "UX Designer Senior", location: "Bordeaux", skills: ["Figma", "User Research", "Prototyping", "Design System"] },
    { name: "Julien Bernard", title: "Data Analyst", location: "Toulouse", skills: ["Python", "SQL", "Tableau", "Machine Learning"] },
    { name: "Camille Petit", title: "Responsable Marketing", location: "Nantes", skills: ["SEO", "Google Ads", "Content Strategy", "Analytics"] },
    { name: "Lucas Moreau", title: "DevOps Engineer", location: "Remote", skills: ["Docker", "Kubernetes", "AWS", "CI/CD"] },
    { name: "Emma Robert", title: "Business Developer", location: "Marseille", skills: ["Négociation", "CRM", "B2B", "Prospection"] },
    { name: "Antoine Garcia", title: "Comptable Senior", location: "Paris", skills: ["Sage", "Bilan", "Fiscalité", "Audit"] },
  ];
  const sources: VeilleProfile["source"][] = ["linkedin", "indeed", "welcometothejungle", "apec"];
  return names.map((n, i) => ({
    id: `veille-${i}`,
    ...n,
    score: Math.floor(Math.random() * 40) + 60,
    source: sources[i % sources.length],
    matchReason: `Profil correspondant à "${jobs[i % jobs.length]?.title || "vos besoins"}"`,
  }));
};

export default function RHDashboard() {
  const { user } = useAuth();
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [jobPostings, setJobPostings] = useState<JobPosting[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showAddCandidate, setShowAddCandidate] = useState(false);
  const [showAddJob, setShowAddJob] = useState(false);
  const [showAIJob, setShowAIJob] = useState(false);
  const [aiJobPrompt, setAiJobPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [analyzingCv, setAnalyzingCv] = useState<string | null>(null);
  const [veilleSearch, setVeilleSearch] = useState("");
  const [veilleScanning, setVeilleScanning] = useState(false);
  const [veilleProfiles, setVeilleProfiles] = useState<VeilleProfile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadTargetId, setUploadTargetId] = useState<string | null>(null);

  const [newCandidate, setNewCandidate] = useState({
    first_name: "", last_name: "", email: "", phone: "", notes: "", source: "manual",
  });

  const [newJob, setNewJob] = useState({
    title: "", description: "", requirements: "", location: "", contract_type: "CDI", salary_range: "",
  });

  useEffect(() => {
    if (user) fetchAll();
  }, [user]);

  const fetchAll = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [cRes, jRes] = await Promise.all([
        fetch(`${API_URL}/api/rh/candidates`, { credentials: "include" }),
        fetch(`${API_URL}/api/rh/jobs`, { credentials: "include" }),
      ]);
      if (cRes.ok) setCandidates((await cRes.json()).candidates || []);
      if (jRes.ok) {
        const jobs = (await jRes.json()).jobs || [];
        setJobPostings(jobs);
        setVeilleProfiles(generateVeilleProfiles(jobs));
      }
    } catch {}
    setLoading(false);
  };

  const addCandidate = async () => {
    if (!user || !newCandidate.first_name) return;
    const res = await fetch(`${API_URL}/api/rh/candidates`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(newCandidate) });
    if (!res.ok) { toast.error("Erreur lors de l'ajout"); return; }
    toast.success("Candidat ajouté !");
    setNewCandidate({ first_name: "", last_name: "", email: "", phone: "", notes: "", source: "manual" });
    setShowAddCandidate(false);
    fetchAll();
  };

  const addJobPosting = async () => {
    if (!user || !newJob.title) return;
    const res = await fetch(`${API_URL}/api/rh/jobs`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(newJob) });
    if (!res.ok) { toast.error("Erreur lors de l'ajout"); return; }
    toast.success("Offre créée !");
    setNewJob({ title: "", description: "", requirements: "", location: "", contract_type: "CDI", salary_range: "" });
    setShowAddJob(false);
    fetchAll();
  };

  const updateCandidateStatus = async (id: string, status: string) => {
    await fetch(`${API_URL}/api/rh/candidates/${id}`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status }) });
    setCandidates(prev => prev.map(c => c.id === id ? { ...c, status } : c));
    toast.success("Statut mis à jour");
  };

  const deleteCandidate = async (id: string) => {
    await fetch(`${API_URL}/api/rh/candidates/${id}`, { method: "DELETE", credentials: "include" });
    setCandidates(prev => prev.filter(c => c.id !== id));
    toast.success("Candidat supprimé");
  };

  const deleteJob = async (id: string) => {
    await fetch(`${API_URL}/api/rh/jobs/${id}`, { method: "DELETE", credentials: "include" });
    setJobPostings(prev => prev.filter(j => j.id !== id));
    toast.success("Offre supprimée");
  };

  const updateJobStatus = async (id: string, status: string) => {
    await fetch(`${API_URL}/api/rh/jobs/${id}`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status }) });
    setJobPostings(prev => prev.map(j => j.id === id ? { ...j, status } : j));
    toast.success("Statut mis à jour");
  };

  const analyzeCvWithAI = async (candidateId: string, candidateName: string) => {
    toast.info("Analyse IA des CV non connectée pour l'instant.");
    return;
    setAnalyzingCv(candidateId);
    try {
      // Simplified AI analysis - update with notes
      const score = Math.floor(Math.random() * 30) + 60;
      await fetch(`${API_URL}/api/rh/candidates/${candidateId}`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ score, notes: `Analyse Grafton IA: Score ${score}/100` }) });
      setCandidates(prev => prev.map(c => c.id === candidateId ? { ...c, score, notes: `Analyse Grafton IA: Score ${score}/100` } : c));
      toast.success(`Score IA : ${score}/100`);
    } catch {
      toast.error("Erreur lors de l'analyse IA");
    }
    setAnalyzingCv(null);
  };

  const handleCVUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    toast.success("CV noté — fonctionnalité upload en développement");
    setUploadTargetId(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const generateJobWithAI = async () => {
    if (!aiJobPrompt.trim()) return;
    setAiLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/chat/module`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message: `Tu es un expert RH. Génère une offre d'emploi complète en JSON avec les champs: title, description, requirements, location, contract_type, salary_range. Voici le besoin: ${aiJobPrompt}`, module: "rh" }) });
      if (res.ok) {
        const data = await res.json();
        try {
          const jsonStr = (data.response || "").match(/\{[\s\S]*\}/)?.[0] || "{}";
          const parsed = JSON.parse(jsonStr);
          setNewJob({
            title: parsed.title || "",
            description: parsed.description || "",
            requirements: parsed.requirements || "",
            location: parsed.location || "",
            contract_type: parsed.contract_type || "CDI",
            salary_range: parsed.salary_range || "",
          });
          setShowAIJob(false);
          setShowAddJob(true);
          toast.success("Offre générée par Grafton IA !");
        } catch { toast.error("Erreur de format IA"); }
      }
    } catch {
      toast.error("Erreur de génération IA");
    }
    setAiLoading(false);
  };

  const runVeilleScan = () => {
    setVeilleScanning(true);
    setTimeout(() => {
      setVeilleProfiles(generateVeilleProfiles(jobPostings));
      setVeilleScanning(false);
      toast.success("Scan terminé ! Nouveaux profils détectés.");
    }, 2500);
  };

  const addVeilleToCandidate = async (profile: VeilleProfile) => {
    if (!user) return;
    const res = await fetch(`${API_URL}/api/rh/candidates`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({
      first_name: profile.name.split(" ")[0],
      last_name: profile.name.split(" ").slice(1).join(" "),
      score: profile.score,
      source: profile.source,
      notes: `${profile.title} - ${profile.location}\nCompétences: ${profile.skills.join(", ")}\n${profile.matchReason}`,
    }) });
    if (!res.ok) { toast.error("Erreur"); return; }
    toast.success(`${profile.name} ajouté aux candidats !`);
    setVeilleProfiles(prev => prev.filter(p => p.id !== profile.id));
    fetchAll();
  };

  const filteredCandidates = candidates.filter(c => {
    const matchSearch = `${c.first_name} ${c.last_name} ${c.email}`.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || c.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const filteredVeille = veilleProfiles.filter(p =>
    `${p.name} ${p.title} ${p.skills.join(" ")}`.toLowerCase().includes(veilleSearch.toLowerCase())
  );

  const stats = {
    total: candidates.length,
    new: candidates.filter(c => c.status === "new").length,
    interview: candidates.filter(c => c.status === "interview").length,
    hired: candidates.filter(c => c.status === "hired").length,
    activeJobs: jobPostings.filter(j => j.status === "published").length,
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <main className="flex-1 p-4 md:p-8 overflow-y-auto">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-2">
                  <Users className="h-7 w-7 text-primary" /> RH & Recrutement
                </h1>
                <p className="text-muted-foreground text-sm mt-1">Gérez vos recrutements, analysez les CV et trouvez les meilleurs talents.</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => setShowAIJob(true)} className="gap-1.5">
                  <Bot className="h-4 w-4" /> Grafton rédige
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowAddJob(true)} className="gap-1.5">
                  <Briefcase className="h-4 w-4" /> Nouvelle offre
                </Button>
                <Button size="sm" onClick={() => setShowAddCandidate(true)} className="gap-1.5">
                  <UserPlus className="h-4 w-4" /> Ajouter candidat
                </Button>
              </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { label: "Candidats", value: stats.total, icon: Users, color: "text-blue-400" },
                { label: "Nouveaux", value: stats.new, icon: AlertCircle, color: "text-amber-400" },
                { label: "Entretiens", value: stats.interview, icon: Eye, color: "text-purple-400" },
                { label: "Recrutés", value: stats.hired, icon: CheckCircle2, color: "text-emerald-400" },
                { label: "Offres actives", value: stats.activeJobs, icon: Briefcase, color: "text-primary" },
              ].map(kpi => (
                <Card key={kpi.label} className="p-4 flex flex-col items-center gap-1 bg-card/50 border-border/50">
                  <kpi.icon className={`h-5 w-5 ${kpi.color}`} />
                  <span className="text-2xl font-bold text-foreground">{kpi.value}</span>
                  <span className="text-xs text-muted-foreground">{kpi.label}</span>
                </Card>
              ))}
            </div>

            {/* Tabs */}
            <Tabs defaultValue="candidates" className="space-y-4">
              <TabsList className="bg-muted/50">
                <TabsTrigger value="candidates" className="gap-1.5"><Users className="h-3.5 w-3.5" /> Candidats</TabsTrigger>
                <TabsTrigger value="jobs" className="gap-1.5"><Briefcase className="h-3.5 w-3.5" /> Offres d'emploi</TabsTrigger>
                <TabsTrigger value="veille" className="gap-1.5"><Radar className="h-3.5 w-3.5" /> Veille</TabsTrigger>
                <TabsTrigger value="pipeline" className="gap-1.5"><BarChart3 className="h-3.5 w-3.5" /> Pipeline</TabsTrigger>
              </TabsList>

              {/* Candidates Tab */}
              <TabsContent value="candidates" className="space-y-4">
                <div className="flex gap-2 flex-wrap">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input placeholder="Rechercher un candidat..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[160px]"><Filter className="h-3.5 w-3.5 mr-1.5" /><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {loading ? (
                  <div className="text-center py-12 text-muted-foreground">Chargement...</div>
                ) : filteredCandidates.length === 0 ? (
                  <Card className="p-12 text-center border-dashed">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                    <p className="text-muted-foreground">Aucun candidat pour le moment</p>
                    <Button size="sm" className="mt-4" onClick={() => setShowAddCandidate(true)}>
                      <UserPlus className="h-4 w-4 mr-1.5" /> Ajouter un candidat
                    </Button>
                  </Card>
                ) : (
                  <div className="grid gap-3">
                    <AnimatePresence>
                      {filteredCandidates.map(c => {
                        const st = STATUS_CONFIG[c.status] || STATUS_CONFIG.new;
                        const Icon = st.icon;
                        const isAnalyzing = analyzingCv === c.id;
                        return (
                          <motion.div key={c.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                            <Card className="p-4 flex flex-col md:flex-row md:items-center gap-3 bg-card/50 border-border/50 hover:border-primary/30 transition-colors">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                  <span className="text-sm font-bold text-primary">{c.first_name[0]}{c.last_name[0]}</span>
                                </div>
                                <div className="min-w-0">
                                  <p className="font-medium text-foreground truncate">{c.first_name} {c.last_name}</p>
                                  <p className="text-xs text-muted-foreground truncate">{c.email || "Pas d'email"}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                {c.score > 0 && (
                                  <Badge variant="outline" className="gap-1 text-amber-400 border-amber-500/20">
                                    <Star className="h-3 w-3" /> {c.score}/100
                                  </Badge>
                                )}
                                <Badge variant="outline" className={st.color}>
                                  <Icon className="h-3 w-3 mr-1" /> {st.label}
                                </Badge>
                                {c.cv_url && (
                                  <a href={c.cv_url} target="_blank" rel="noreferrer">
                                    <Badge variant="outline" className="gap-1 cursor-pointer hover:bg-primary/10">
                                      <FileText className="h-3 w-3" /> CV
                                    </Badge>
                                  </a>
                                )}
                                <Button
                                  variant="ghost" size="sm"
                                  className="h-7 text-xs gap-1 text-primary"
                                  disabled={isAnalyzing}
                                  onClick={() => analyzeCvWithAI(c.id, `${c.first_name} ${c.last_name}`)}
                                >
                                  {isAnalyzing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                                  {isAnalyzing ? "Analyse..." : "Scorer IA"}
                                </Button>
                                <Button
                                  variant="ghost" size="icon" className="h-7 w-7"
                                  onClick={() => { setUploadTargetId(c.id); fileInputRef.current?.click(); }}
                                  title="Uploader un CV"
                                >
                                  <Upload className="h-3.5 w-3.5" />
                                </Button>
                                <Select value={c.status} onValueChange={v => updateCandidateStatus(c.id, v)}>
                                  <SelectTrigger className="h-7 w-[130px] text-xs"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive/60 hover:text-destructive" onClick={() => deleteCandidate(c.id)}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </Card>
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                  </div>
                )}
                <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleCVUpload} />
              </TabsContent>

              {/* Job Postings Tab */}
              <TabsContent value="jobs" className="space-y-4">
                {jobPostings.length === 0 ? (
                  <Card className="p-12 text-center border-dashed">
                    <Briefcase className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                    <p className="text-muted-foreground">Aucune offre d'emploi</p>
                    <div className="flex gap-2 justify-center mt-4">
                      <Button size="sm" variant="outline" onClick={() => setShowAIJob(true)}>
                        <Bot className="h-4 w-4 mr-1.5" /> Grafton rédige
                      </Button>
                      <Button size="sm" onClick={() => setShowAddJob(true)}>
                        <Plus className="h-4 w-4 mr-1.5" /> Créer une offre
                      </Button>
                    </div>
                  </Card>
                ) : (
                  <div className="grid gap-3 md:grid-cols-2">
                    {jobPostings.map(j => {
                      const jst = JOB_STATUS_CONFIG[j.status] || JOB_STATUS_CONFIG.draft;
                      return (
                        <Card key={j.id} className="p-5 bg-card/50 border-border/50 hover:border-primary/30 transition-colors space-y-3">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <h3 className="font-semibold text-foreground">{j.title}</h3>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                                {j.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{j.location}</span>}
                                <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{j.contract_type}</span>
                              </div>
                            </div>
                            <Badge variant="outline" className={jst.color}>{jst.label}</Badge>
                          </div>
                          {j.description && <p className="text-xs text-muted-foreground line-clamp-2">{j.description}</p>}
                          {j.salary_range && <p className="text-xs text-primary font-medium">{j.salary_range}</p>}
                          <div className="flex items-center gap-2 pt-1">
                            <Select value={j.status} onValueChange={v => updateJobStatus(j.id, v)}>
                              <SelectTrigger className="h-7 text-xs flex-1"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {Object.entries(JOB_STATUS_CONFIG).map(([k, v]) => (
                                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive/60 hover:text-destructive" onClick={() => deleteJob(j.id)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              {/* Veille Tab */}
              <TabsContent value="veille" className="space-y-4">
                <Card className="p-5 bg-gradient-to-br from-primary/5 to-transparent border-primary/20">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Radar className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">Veille Recrutement IA</h3>
                        <p className="text-xs text-muted-foreground">Surveillance automatique des profils sur LinkedIn, Indeed, WTTJ et APEC</p>
                      </div>
                    </div>
                    <Button onClick={runVeilleScan} disabled={veilleScanning} className="gap-1.5">
                      {veilleScanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                      {veilleScanning ? "Scan en cours..." : "Lancer un scan"}
                    </Button>
                  </div>
                </Card>

                {/* Platform status */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(SOURCE_ICONS).map(([key, src]) => {
                    const count = veilleProfiles.filter(p => p.source === key).length;
                    return (
                      <Card key={key} className="p-3 flex items-center gap-3 bg-card/50 border-border/50">
                        <src.icon className={`h-5 w-5 ${src.color}`} />
                        <div>
                          <p className="text-sm font-medium text-foreground">{src.label}</p>
                          <p className="text-xs text-muted-foreground">{count} profils détectés</p>
                        </div>
                      </Card>
                    );
                  })}
                </div>

                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Filtrer par nom, titre, compétence..." value={veilleSearch} onChange={e => setVeilleSearch(e.target.value)} className="pl-9" />
                </div>

                {/* Profiles */}
                {veilleScanning ? (
                  <Card className="p-12 text-center border-dashed">
                    <Loader2 className="h-8 w-8 mx-auto text-primary animate-spin mb-4" />
                    <p className="text-muted-foreground">Scan des plateformes en cours...</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">LinkedIn, Indeed, Welcome to the Jungle, APEC</p>
                  </Card>
                ) : filteredVeille.length === 0 ? (
                  <Card className="p-12 text-center border-dashed">
                    <Radar className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                    <p className="text-muted-foreground">Aucun profil détecté</p>
                    <p className="text-xs text-muted-foreground/60 mt-1">Publiez des offres d'emploi puis lancez un scan pour détecter des profils pertinents</p>
                  </Card>
                ) : (
                  <div className="grid gap-3 md:grid-cols-2">
                    <AnimatePresence>
                      {filteredVeille.map((p, i) => {
                        const src = SOURCE_ICONS[p.source] || SOURCE_ICONS.linkedin;
                        return (
                          <motion.div key={p.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                            <Card className="p-4 bg-card/50 border-border/50 hover:border-primary/30 transition-colors space-y-3">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex items-center gap-3">
                                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                    <span className="text-sm font-bold text-primary">{p.name.split(" ").map(n => n[0]).join("")}</span>
                                  </div>
                                  <div>
                                    <p className="font-medium text-foreground">{p.name}</p>
                                    <p className="text-xs text-muted-foreground">{p.title}</p>
                                  </div>
                                </div>
                                <Badge variant="outline" className={`gap-1 ${p.score >= 80 ? "text-emerald-400 border-emerald-500/20" : p.score >= 60 ? "text-amber-400 border-amber-500/20" : "text-red-400 border-red-500/20"}`}>
                                  <Target className="h-3 w-3" /> {p.score}%
                                </Badge>
                              </div>

                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <MapPin className="h-3 w-3" /> {p.location}
                                <span className="mx-1">•</span>
                                <src.icon className={`h-3 w-3 ${src.color}`} /> {src.label}
                              </div>

                              <div className="flex flex-wrap gap-1.5">
                                {p.skills.map(s => (
                                  <Badge key={s} variant="secondary" className="text-[10px] px-1.5 py-0">{s}</Badge>
                                ))}
                              </div>

                              <p className="text-xs text-muted-foreground/80 flex items-center gap-1">
                                <TrendingUp className="h-3 w-3 text-primary" /> {p.matchReason}
                              </p>

                              <div className="flex gap-2 pt-1">
                                <Button size="sm" variant="outline" className="flex-1 h-7 text-xs gap-1" onClick={() => addVeilleToCandidate(p)}>
                                  <UserPlus className="h-3 w-3" /> Ajouter aux candidats
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 text-xs gap-1">
                                  <ExternalLink className="h-3 w-3" /> Profil
                                </Button>
                              </div>
                            </Card>
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                  </div>
                )}
              </TabsContent>

              {/* Pipeline Tab */}
              <TabsContent value="pipeline" className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                    const count = candidates.filter(c => c.status === key).length;
                    const PIcon = config.icon;
                    return (
                      <Card key={key} className="p-4 space-y-3 bg-card/50 border-border/50">
                        <div className="flex items-center gap-2">
                          <PIcon className="h-4 w-4" />
                          <span className="text-sm font-medium">{config.label}</span>
                          <Badge variant="secondary" className="ml-auto text-xs">{count}</Badge>
                        </div>
                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                          {candidates.filter(c => c.status === key).map(c => (
                            <div key={c.id} className="p-2 rounded-md bg-muted/30 border border-border/30 text-xs">
                              <p className="font-medium text-foreground">{c.first_name} {c.last_name}</p>
                              <p className="text-muted-foreground truncate">{c.email}</p>
                              {c.score > 0 && (
                                <div className="flex items-center gap-1 mt-1 text-amber-400">
                                  <Star className="h-3 w-3" /> {c.score}/100
                                </div>
                              )}
                            </div>
                          ))}
                          {count === 0 && <p className="text-xs text-muted-foreground/50 text-center py-4">Vide</p>}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </TabsContent>
            </Tabs>
          </motion.div>

          {/* Add Candidate Dialog */}
          <Dialog open={showAddCandidate} onOpenChange={setShowAddCandidate}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Ajouter un candidat</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Prénom" value={newCandidate.first_name} onChange={e => setNewCandidate(p => ({ ...p, first_name: e.target.value }))} />
                  <Input placeholder="Nom" value={newCandidate.last_name} onChange={e => setNewCandidate(p => ({ ...p, last_name: e.target.value }))} />
                </div>
                <Input placeholder="Email" value={newCandidate.email} onChange={e => setNewCandidate(p => ({ ...p, email: e.target.value }))} />
                <Input placeholder="Téléphone" value={newCandidate.phone} onChange={e => setNewCandidate(p => ({ ...p, phone: e.target.value }))} />
                <Select value={newCandidate.source} onValueChange={v => setNewCandidate(p => ({ ...p, source: v }))}>
                  <SelectTrigger><SelectValue placeholder="Source" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Ajout manuel</SelectItem>
                    <SelectItem value="linkedin">LinkedIn</SelectItem>
                    <SelectItem value="indeed">Indeed</SelectItem>
                    <SelectItem value="recommendation">Recommandation</SelectItem>
                    <SelectItem value="website">Site web</SelectItem>
                  </SelectContent>
                </Select>
                <Textarea placeholder="Notes..." value={newCandidate.notes} onChange={e => setNewCandidate(p => ({ ...p, notes: e.target.value }))} rows={3} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddCandidate(false)}>Annuler</Button>
                <Button onClick={addCandidate} disabled={!newCandidate.first_name}><UserPlus className="h-4 w-4 mr-1.5" /> Ajouter</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Add Job Dialog */}
          <Dialog open={showAddJob} onOpenChange={setShowAddJob}>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Créer une offre d'emploi</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input placeholder="Titre du poste" value={newJob.title} onChange={e => setNewJob(p => ({ ...p, title: e.target.value }))} />
                <Textarea placeholder="Description du poste..." value={newJob.description} onChange={e => setNewJob(p => ({ ...p, description: e.target.value }))} rows={4} />
                <Textarea placeholder="Compétences requises..." value={newJob.requirements} onChange={e => setNewJob(p => ({ ...p, requirements: e.target.value }))} rows={3} />
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Lieu" value={newJob.location} onChange={e => setNewJob(p => ({ ...p, location: e.target.value }))} />
                  <Select value={newJob.contract_type} onValueChange={v => setNewJob(p => ({ ...p, contract_type: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CDI">CDI</SelectItem>
                      <SelectItem value="CDD">CDD</SelectItem>
                      <SelectItem value="Stage">Stage</SelectItem>
                      <SelectItem value="Alternance">Alternance</SelectItem>
                      <SelectItem value="Freelance">Freelance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Input placeholder="Fourchette salariale (ex: 35-45K€)" value={newJob.salary_range} onChange={e => setNewJob(p => ({ ...p, salary_range: e.target.value }))} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddJob(false)}>Annuler</Button>
                <Button onClick={addJobPosting} disabled={!newJob.title}><Briefcase className="h-4 w-4 mr-1.5" /> Créer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* AI Job Generator Dialog */}
          <Dialog open={showAIJob} onOpenChange={setShowAIJob}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-primary" /> Grafton rédige votre offre
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Décrivez le profil que vous recherchez et Grafton rédigera l'offre d'emploi complète.</p>
                <Textarea
                  placeholder="Ex: Je cherche un développeur React senior à Paris, expérience 5 ans minimum, secteur fintech..."
                  value={aiJobPrompt}
                  onChange={e => setAiJobPrompt(e.target.value)}
                  rows={4}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAIJob(false)}>Annuler</Button>
                <Button onClick={generateJobWithAI} disabled={!aiJobPrompt.trim() || aiLoading}>
                  {aiLoading ? "Génération..." : <><Sparkles className="h-4 w-4 mr-1.5" /> Générer</>}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <GraftonChat
            variant="rh"
            context={{ module: "rh-recrutement" }}
            subtitle="DRH virtuel — équipe humaine + 9 agents IA"
            welcomeMessage="Je peux vous aider à rédiger des offres d'emploi, analyser des CV et optimiser vos recrutements."
            quickPrompts={["Rédige une offre d'emploi", "Conseils pour un entretien", "Analyse ce profil candidat", "Optimise mon processus RH"]}
          />
        </main>
      </div>
    </SidebarProvider>
  );
}
